import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

/** Segment's cookie and localStorage key, so Segment (and other Kin sites) pick up the same ID */
export const ANONYMOUS_ID_KEY = 'ajs_anonymous_id';
const COOKIE_DAYS = 365;
const COOKIE_DOMAINS = ['localhost', 'kin.com', 'kin.co', 'kin.haus', 'kin.insure'];

/** The root Kin domain for a hostname (kin.com when it isn't one), so every Kin subdomain shares the cookie */
export function cookieDomainFor(hostname: string): string {
  return COOKIE_DOMAINS.find(domain => hostname === domain || hostname.endsWith(`.${domain}`)) ?? 'kin.com';
}

/**
 * A anonymous ID for the visitor, set from the app initializer so it's there for analytics (Segment, or
 * another vendor) as a cookie on the root Kin domain. So, Segment and later Kin pages can use the same ID.
 */
@Injectable({ providedIn: 'root' })
export class AnonymousIdService {
  private document = inject(DOCUMENT);
  private current = '';

  /** Created on first read when the initializer hasn't run */
  get id(): string {
    if (!this.current) this.initialize();
    return this.current;
  }

  initialize(): void {
    const existing = this.readCookie();
    // Segment stores the ID JSON-encoded ("%22…%22")
    this.current = existing ? decodeURIComponent(existing).replace(/(^"|"$)/g, '') : this.create();
  }

  private create(): string {
    const view = this.document.defaultView;
    if (!view) return '';
    const id = view.crypto.randomUUID();
    const expires = new Date();
    expires.setDate(expires.getDate() + COOKIE_DAYS);
    const domain = cookieDomainFor(view.location.hostname);
    this.document.cookie =
      `${ANONYMOUS_ID_KEY}=${id}; Domain=${domain}; Expires=${expires.toUTCString()}; Path=/; SameSite=Lax; Secure`;
    // Segment's own formats: the cookie as-is, the localStorage copy JSON-encoded
    try {
      view.localStorage.setItem(ANONYMOUS_ID_KEY, JSON.stringify(id));
    } catch {
      console.error('Failed to write the anonymous ID to localStorage');
    }
    return id;
  }

  private readCookie(): string | undefined {
    const prefix = `${ANONYMOUS_ID_KEY}=`;
    return this.document.cookie.split('; ').find(cookie => cookie.startsWith(prefix))?.slice(prefix.length);
  }
}
